import { useSyncExternalStore } from 'react'

/**
 * Quantos pixels do fundo da janela estão **cobertos** — na prática, o teclado.
 *
 * ## Por que `position: fixed` não basta
 *
 * No iOS o teclado **não encolhe** o layout viewport: ele aparece por cima.
 * Um `fixed inset-0` continua medindo a janela inteira, então a barra de baixo
 * de um painel de tela cheia vai parar atrás do teclado — visto no iPhone, com
 * o botão de criar a família invisível justamente enquanto se digita o nome
 * dela, que é o único momento em que ele serve.
 *
 * Quem sabe o tamanho de verdade é a `visualViewport`: ela é a parte que a
 * pessoa realmente vê, e encolhe. A conta é a diferença entre as duas, mais o
 * `offsetTop` — o iOS **rola** o layout viewport quando o campo focado está
 * perto do fim, e sem esse termo a barra descolaria do teclado ao rolar.
 *
 * ## `useSyncExternalStore`, e não `useState` + `useEffect`
 *
 * Isto é uma assinatura de fonte externa, que é exatamente o que este gancho
 * existe para ler. A versão com estado precisaria escrever no efeito — o que
 * `react-hooks/set-state-in-effect` proíbe, com razão: renderiza uma vez com o
 * valor errado antes de corrigir, e a barra pularia na abertura.
 *
 * ## Onde ele dá zero, e por que isso é o certo
 *
 * Sem `visualViewport` (navegador antigo, jsdom) e no Android — onde o teclado
 * encolhe o layout viewport de verdade — a conta dá zero, e o painel fica como
 * era. É degradação para o comportamento anterior, não para um quebrado.
 */
export function useTecladoCobrindo(): number {
  return useSyncExternalStore(assinar, ler, () => 0)
}

function assinar(aoMudar: () => void): () => void {
  const vv = window.visualViewport
  if (vv == null) return () => undefined
  vv.addEventListener('resize', aoMudar)
  // O `scroll` também: o iOS rola o layout viewport para trazer o campo focado
  // à vista, e só o `resize` deixaria a barra para trás nesse movimento.
  vv.addEventListener('scroll', aoMudar)
  return () => {
    vv.removeEventListener('resize', aoMudar)
    vv.removeEventListener('scroll', aoMudar)
  }
}

function ler(): number {
  const vv = window.visualViewport
  if (vv == null) return 0
  // Arredondado: o iOS devolve fração de pixel, e um valor novo a cada frame
  // faria `useSyncExternalStore` renderizar sem nada ter mudado de verdade.
  const coberto = Math.round(window.innerHeight - vv.height - vv.offsetTop)
  return coberto > 0 ? coberto : 0
}
